import { Component, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription, takeUntil } from 'rxjs';
import { EnvService } from '../../configuration/env.service';
import { LocationContextService } from '../../location-context/location-context.service';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import {
    IMopOperatorsMessageUser,
    MopOperatorsDialogComponent
} from '../conmon/mop-operators-dialog/mop-operators-dialog.component';
import { CONFIRMATION_COMMANDS, ICommand, IDockingContext, IField, IUserMonitoring } from '../../../../model/docking';
import { DockingService } from '../docking.service';
import { AuthenticationService } from '../../authentication/authentication.service';
import { LogService } from '../../logging/log.service';
import { FopModeService } from '../../fop-mode/fop-mode.service';
import { CommandPinCodeComponent } from '../command-pin-code/command-pin-code.component';
import { BasicComponent } from '../../basic-component';
import { take } from 'rxjs/operators';
import { ConMonService } from '../conmon/conmon.service';
import { ClientsService } from '../../clients/clients.service';
import { MonitoringModeDialogComponent } from '../conmon/monitoring-mode-dialog/monitoring-mode-dialog.component';
import { GeneralMessageService } from '../../shared/general-message/general-message.service';

const CON_MON_STATUS_INTERVAL = 200;

@Component({
    selector: 'app-docking-commands',
    templateUrl: './docking-commands.component.html',
    styleUrls: ['./docking-commands.component.scss']
})
export class DockingCommandsComponent extends BasicComponent implements OnInit, OnChanges, OnDestroy {
    @Input() dockingContext: IDockingContext;
    LOG_SOURCE = 'DockingCommandsComponent';
    operator: string;
    roles: string[];

    arrayOfIssuedCommands: ICommand[];

    // Temporary disables buttons to avoid double clicks
    buttonsDisabled = false;
    pinCodeSuccessTimeout: number;
    pinCodeRequired: boolean;
    isFopMode = true;
    userIsMonitoringDocking = false;
    userId: string;
    dialogOpen = false;
    snackBarOpen = false;
    userHasSelectedIfPartOfConMon = false;
    conMonStatusInterval: NodeJS.Timer;
    slowConnectionDetectionSubscription$: Subscription | undefined = undefined;

    mopOperatorsDialogRef: MatDialogRef<MopOperatorsDialogComponent> | undefined = undefined;

    constructor(
        private readonly dockingService: DockingService,
        private readonly conMonService: ConMonService,
        private readonly locationContextService: LocationContextService,
        private readonly authenticationService: AuthenticationService,
        private readonly fopModeService: FopModeService,
        private readonly env: EnvService,
        public readonly dialog: MatDialog,
        private readonly logService: LogService,
        private readonly clientService: ClientsService,
        private readonly generalMessageService: GeneralMessageService
    ) {
        super();
        this.pinCodeSuccessTimeout = this.env.config.docking.pinCodeSuccessTimeout;
    }

    ngOnInit() {
        this.arrayOfIssuedCommands = [];

        this.userId = this.clientService.getCorrelationId();
        this.operator = this.authenticationService.getUsername();
        this.roles = this.authenticationService.getUserRoles();
        if (!this.operator) {
            throw new Error('Operator is undefined');
        }

        this.fopModeService
            .getFopConfig()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(fopConfig => {
                this.isFopMode = fopConfig.fopModeEnabled;
            });

        if (this.userShouldSelectIfPartOfConMon()) {
            this.openConMonDialog();
        }
    }

    openMonitoringMopOperatorsDialog(): void {
        this.mopOperatorsDialogRef = this.dialog.open(MopOperatorsDialogComponent, {
            data: {
                aliveMonitors: this.dockingContext.aliveMonitors,
                requiredMonitors: this.dockingContext.requiredMonitors,
                usersMonitoring: this.usersMonitoringToOverrideMessage(this.dockingContext.usersMonitoring)
            }
        });

        this.dialogOpen = true;

        this.mopOperatorsDialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe(_ => {
                this.dialogOpen = false;
                this.mopOperatorsDialogRef = undefined;
            });
    }

    openConMonDialog(): void {
        const joinMonDialog = this.dialog.open(MonitoringModeDialogComponent);

        this.dialogOpen = true;

        joinMonDialog
            .afterClosed()
            .pipe(take(1))
            .subscribe(result => {
                this.dialogOpen = false;
                this.userHasSelectedIfPartOfConMon = true;
                if (result) {
                    this.waitForDockingResponse();
                    this.startMonitorDocking();
                } else if (this.conMonRequired(this.dockingContext)) {
                    this.buttonsDisabled = true;
                }
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.conMonRequired(this.dockingContext)) {
            this.handleConMonChange();
        }
    }

    // TODO: Sync within team on how we can update FOP config to take restrictions into account for a long-term solution.
    // TODO: Will be important for when SAM should integrated.
    commandAllowedOnDevice(command: ICommand): boolean {
        return (
            !command?.restrictions ||
            command.restrictions.length === 0 ||
            (command?.restrictions?.includes('FOP') && this.isFopMode) ||
            (command?.restrictions?.includes('MOP') && !this.isFopMode)
        );
    }

    commandsShouldBeVisible(): boolean {
        return !(this.conMonRequired(this.dockingContext) && !this.userIsMonitoringDocking);
    }

    triggerDockingCommand(command: ICommand) {
        if (CONFIRMATION_COMMANDS.includes(command.catg)) {
            const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                data: `Are you sure you want to execute command ${command.text}?`
            });
            dialogRef.afterClosed().subscribe(result => {
                if (result) {
                    this.executeDockingCommand(command);
                }
            });
        } else {
            this.executeDockingCommand(command);
        }
    }

    executeDockingCommand(command: ICommand) {
        if (command.SelectionList) {
            this.arrayOfIssuedCommands.push(command);
            this.logStateOfCommandsArray();
        }
        //check if the command requires a pin code
        if (command.PIN) {
            this.pinCodeRequired = true;
        }
        // Bypasses the selection of 'start docking' button to get to the aircraft selection
        // menu without entering pin code - this if statement will only fire AFTER the ac type has
        // been selected
        if (!command.SelectionList && this.pinCodeRequired) {
            //if running as MOP use hardcoded pin code value
            if (!this.isFopMode) {
                // Execute docking command when PIN was stored via CommandPinCodeComponent
                const pin = 'MOP';
                const fields = this.addPinCodeToFields(command.fields, pin);
                this.dockingService.triggerDockingCommand(
                    this.locationContextService.getLocationContext(),
                    { ...command, fields },
                    this.operator,
                    this.isFopMode,
                    this.dockingContext.info
                );
            } else {
                const dialogRef = this.dialog.open(CommandPinCodeComponent, {
                    disableClose: false,
                    data: {
                        userPinStorageService: this.dockingService
                    }
                });
                dialogRef.afterClosed().subscribe(() => {
                    // Execute docking command when PIN was stored via CommandPinCodeComponent
                    const pin = this.dockingService.userEnteredPin;
                    const fields = this.addPinCodeToFields(command.fields, pin);
                    this.dockingService.triggerDockingCommand(
                        this.locationContextService.getLocationContext(),
                        { ...command, fields },
                        this.operator,
                        this.isFopMode,
                        this.dockingContext.info
                    );
                    this.dockingService.clearUserPin();
                    this.checkIfPinAccepted();
                    this.pinCodeRequired = false;
                });
                this.buttonsDisabled = true;
                this.waitForDockingResponse();
            }
            // normal docking command without pin code
        } else {
            this.dockingService.triggerDockingCommand(
                this.locationContextService.getLocationContext(),
                command,
                this.operator,
                this.isFopMode,
                this.dockingContext.info
            );
            this.buttonsDisabled = true;
            this.waitForDockingResponse();
        }
    }

    triggerBackCommand() {
        this.dockingService.triggerBackCommand(this.locationContextService.getLocationContext());

        this.logStateOfCommandsArray();
    }

    triggerDockingCommandGoBack(command: ICommand) {
        this.dockingService.triggerDockingCommand(
            this.locationContextService.getLocationContext(),
            command,
            this.operator,
            this.isFopMode,
            this.dockingContext.info
        );
    }

    goBack() {
        /** Menu #0 is a special case, we can go back but then we will have to use
         *  this.triggerBackCommand() **/

        // Since we go back, we don't this for the future, and throw it away
        const poppedCommand = this.arrayOfIssuedCommands.pop();
        this.logPoppedCommand(poppedCommand);
        if (poppedCommand?.SelectionList) {
            if (this.arrayOfIssuedCommands.length < 1) {
                this.triggerBackCommand();
            } else {
                this.triggerDockingCommandGoBack(this.arrayOfIssuedCommands[this.arrayOfIssuedCommands.length - 1]);
            }
        } else if (this.arrayOfIssuedCommands.length > 1) {
            this.triggerDockingCommandGoBack(this.arrayOfIssuedCommands[this.arrayOfIssuedCommands.length - 1]);
        } else if (this.arrayOfIssuedCommands.length <= 1) {
            this.triggerBackCommand();
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.stopMonitorDocking();
    }

    @HostListener('document:visibilitychange', ['$event'])
    private stopConMonOnHiddenDocument(_) {
        if (document.hidden) {
            this.stopMonitorDocking();
        } else if (this.userShouldSelectIfPartOfConMon()) {
            this.openConMonDialog();
        }
    }

    private handleConMonChange() {
        if (this.userShouldSelectIfPartOfConMon()) {
            this.openConMonDialog();
        }

        if (this.dockingContext.conMonActive && this.userIsMonitoringDocking) {
            this.startMonitorDocking();
            this.handleSlowConnections();
        } else {
            this.stopConMonStatusInterval();
        }

        if (this.mopOperatorsDialogRef) {
            this.updateMopOverrideDialogData(this.dockingContext);
        }
    }

    private handleSlowConnections() {
        if (!this.slowConnectionDetectionSubscription$) {
            this.slowConnectionDetectionSubscription$ = this.dockingService
                .isSlowConnection$()
                .subscribe(isSlowConnection => {
                    if (isSlowConnection && !this.snackBarOpen) {
                        this.generalMessageService.showErrorSnackBar(
                            {
                                title: 'Bad connection detected',
                                message: 'Your device is impacting the docking process.'
                            },
                            false
                        );
                        this.snackBarOpen = true;
                    } else if (!isSlowConnection && this.snackBarOpen) {
                        this.generalMessageService.closeOpenSnackBar();
                        this.snackBarOpen = false;
                    }
                });
        }
    }

    public conMonRequired(dockingContext: IDockingContext) {
        return dockingContext.conMonUsed && this.isFopMode !== undefined && !this.isFopMode;
    }

    private updateMopOverrideDialogData(dockingContext: IDockingContext) {
        this.mopOperatorsDialogRef.componentInstance.mopOperatorsMessage = {
            aliveMonitors: dockingContext.aliveMonitors,
            requiredMonitors: dockingContext.requiredMonitors,
            usersMonitoring: this.usersMonitoringToOverrideMessage(dockingContext.usersMonitoring)
        };
    }

    private usersMonitoringToOverrideMessage(usersMonitoring: IUserMonitoring[]): IMopOperatorsMessageUser[] {
        return usersMonitoring.map(
            user =>
                ({
                    ...user,
                    currentUser: this.conMonService.isCurrentUser(user)
                } as IMopOperatorsMessageUser)
        );
    }

    private startMonitorDocking(): void {
        this.userIsMonitoringDocking = true;
        this.dockingService.setUserMonitoring(this.userIsMonitoringDocking);
        this.startConMonStatusInterval();
    }

    private stopMonitorDocking(): void {
        this.userIsMonitoringDocking = false;
        this.dockingService.setUserMonitoring(this.userIsMonitoringDocking);
        this.userHasSelectedIfPartOfConMon = false;
        this.buttonsDisabled = this.conMonRequired(this.dockingContext) && !this.userIsMonitoringDocking;
        this.stopConMonStatusInterval();
    }

    private startConMonStatusInterval(): void {
        if (this.dockingContext.conMonActive && !this.conMonStatusInterval) {
            this.conMonStatusInterval = setInterval(() => {
                this.conMonService.sendStatusMessage(this.authenticationService.getUsername(), this.operator);
            }, CON_MON_STATUS_INTERVAL);
        }
    }

    private stopConMonStatusInterval(): void {
        clearInterval(this.conMonStatusInterval);
        this.conMonStatusInterval = undefined;
        this.slowConnectionDetectionSubscription$?.unsubscribe();
        this.slowConnectionDetectionSubscription$ = undefined;
        this.dockingService.resetSlowConnectionState();
        this.generalMessageService.closeOpenSnackBar();
        this.snackBarOpen = false;
    }

    private userShouldSelectIfPartOfConMon(): boolean {
        return (
            this.conMonRequired(this.dockingContext) &&
            !this.userIsMonitoringDocking &&
            !this.dialogOpen &&
            !this.userHasSelectedIfPartOfConMon
        );
    }

    private logStateOfCommandsArray() {
        this.logService.info(this.LOG_SOURCE, `Array of commands issued: `, this.arrayOfIssuedCommands);
    }

    private logPoppedCommand(poppedCommand: ICommand) {
        this.logService.info(this.LOG_SOURCE, `popped (thrown away): `, poppedCommand);
    }

    // a timer to check the dgs state after entering a pin code since we don't get a response
    // if it is wrong
    // @TODO check for changes in state instead of specific states
    private checkIfPinAccepted() {
        setTimeout(() => {
            if (this.dockingContext.state === 'Ready') {
                this.triggerBackCommand();
            }
        }, this.pinCodeSuccessTimeout);
    }

    private waitForDockingResponse() {
        setTimeout(() => {
            this, (this.buttonsDisabled = false);
        }, this.pinCodeSuccessTimeout);
    }

    private addPinCodeToFields(fields: IField[] | undefined, pin: string): IField[] {
        const pinField = { key: 'pin', value: pin };
        return fields ? fields.concat(pinField) : [pinField];
    }
}
